/**
 * Operational Scenarios (Atelier 4) DOCX Report Generator (bilingue FR/EN)
 */

import {
  buildCoverPage,
  buildSectionTitle,
  buildParagraph,
  buildBullet,
  buildInfoBox,
  buildTable,
  buildPageBreak,
  buildSpacer,
  buildDocument,
  downloadDocx,
  buildModesOperatoiresFlow,
} from "./docxReportGenerator";
import {
  extractElementaryActions,
  generateModesOperatoiresFromActions,
} from "./scenarioDataNormalizer";
import {
  getSeverityLabelFr,
  getLikelihoodLabelFr,
  getSeverityLabelEn,
  getLikelihoodLabelEn,
  normalizeRiskLevel,
} from "./riskLevelNormalizer";

const LABELS = {
  fr: {
    defaultAnalysis: "Analyse EBIOS RM",
    defaultOrg: "Organisation",
    coverTitle: "Rapport EBIOS RM",
    coverSubtitle: "Atelier 4 \u2014 Sc\u00e9narios Op\u00e9rationnels",
    execSummarySection: "1. R\u00c9SUM\u00c9 EX\u00c9CUTIF",
    nbScenariosBox: "Nombre de sc\u00e9narios",
    nbAttackPathsBox: "Chemins d'attaque",
    highSeverityBox: "Sc\u00e9narios de gravit\u00e9 majeure/critique/catastrophique",
    aiGeneratedBox: "G\u00e9n\u00e9r\u00e9 par IA",
    scenariosSection: "2. D\u00c9TAIL DES SC\u00c9NARIOS OP\u00c9RATIONNELS",
    emptyMsg:
      "Aucun sc\u00e9nario op\u00e9rationnel n'a \u00e9t\u00e9 d\u00e9fini pour cette analyse.",
    scenarioWord: "Sc\u00e9nario",
    severityBox: "S\u00e9v\u00e9rit\u00e9",
    likelihoodBox: "Vraisemblance",
    durationBox: "Dur\u00e9e estim\u00e9e",
    attackPathBox: "Chemin d'attaque",
    sourceBox: "Source",
    generatedByAI: "G\u00e9n\u00e9r\u00e9 par IA",
    contextHeading: "Contexte du Sc\u00e9nario :",
    threatSource: "Source de Menace",
    targetObjective: "Objectif Vis\u00e9",
    dreadedEvent: "\u00c9v\u00e9nement Redout\u00e9",
    supportingAssets: "Biens Supports",
    descriptionHeading: "Description :",
    elementaryActionsHeading:
      "Modes op\u00e9ratoires (Actions \u00e9l\u00e9mentaires) :",
    phasePrefix: "Phase : ",
    phaseLabels: {
      CONNAITRE: "Conna\u00eetre",
      RENTRER: "Rentrer",
      TROUVER: "Trouver",
      EXPLOITER: "Exploiter",
      AGIR: "Agir",
    },
    actionsHeaders: [
      "Technique / Action",
      "Description",
      "Outils",
      "Vraisemblance",
    ],
    dash: "\u2014",
    toolsHeading: "Outils utilis\u00e9s :",
    countermeasuresHeading: "Contre-mesures recommand\u00e9es :",
    appendixSection: "3. ANNEXE \u2014 CHEMINS D'ATTAQUE",
    appendixIntro:
      "Les sc\u00e9narios op\u00e9rationnels ci-dessus ont \u00e9t\u00e9 g\u00e9n\u00e9r\u00e9s \u00e0 partir des chemins d'attaque suivants :",
    apHeaders: [
      "R\u00e9f\u00e9rence",
      "Source de Risque",
      "Objectif Vis\u00e9",
      "S\u00e9v\u00e9rit\u00e9",
    ],
    docTitle: "Rapport Atelier 4 \u2014 Sc\u00e9narios Op\u00e9rationnels",
    footer: "EBIOS RM \u2014 Atelier 4",
    fileBase: "EBIOS-RM_Scenarios-Operationnels",
  },
  en: {
    defaultAnalysis: "EBIOS RM Analysis",
    defaultOrg: "Organization",
    coverTitle: "EBIOS RM Report",
    coverSubtitle: "Workshop 4 \u2014 Operational Scenarios",
    execSummarySection: "1. EXECUTIVE SUMMARY",
    nbScenariosBox: "Number of scenarios",
    nbAttackPathsBox: "Attack paths",
    highSeverityBox: "Major/Critical/Catastrophic severity scenarios",
    aiGeneratedBox: "Generated by AI",
    scenariosSection: "2. OPERATIONAL SCENARIOS DETAIL",
    emptyMsg:
      "No operational scenario has been defined for this analysis.",
    scenarioWord: "Scenario",
    severityBox: "Severity",
    likelihoodBox: "Likelihood",
    durationBox: "Estimated duration",
    attackPathBox: "Attack path",
    sourceBox: "Source",
    generatedByAI: "Generated by AI",
    contextHeading: "Scenario Context:",
    threatSource: "Threat Source",
    targetObjective: "Target Objective",
    dreadedEvent: "Feared Event",
    supportingAssets: "Supporting Assets",
    descriptionHeading: "Description :",
    elementaryActionsHeading: "Operational Steps (Elementary Actions):",
    phasePrefix: "Phase: ",
    phaseLabels: {
      CONNAITRE: "Reconnaissance",
      RENTRER: "Initial Access",
      TROUVER: "Discovery",
      EXPLOITER: "Exploitation",
      AGIR: "Action on Objectives",
    },
    actionsHeaders: [
      "Technique / Action",
      "Description",
      "Tools",
      "Likelihood",
    ],
    dash: "\u2014",
    toolsHeading: "Tools used :",
    countermeasuresHeading: "Recommended countermeasures :",
    appendixSection: "3. APPENDIX \u2014 ATTACK PATHS",
    appendixIntro:
      "The operational scenarios above were generated based on the following attack paths:",
    apHeaders: [
      "Reference",
      "Source of Risk",
      "Target Objective",
      "Severity",
    ],
    docTitle: "Workshop 4 Report \u2014 Operational Scenarios",
    footer: "EBIOS RM \u2014 Workshop 4",
    fileBase: "EBIOS-RM_Operational-Scenarios",
  },
};

/**
 * Generate DOCX report for Operational Scenarios (Atelier 4).
 * @param {Object} reportData - { scenarios, attackPaths, analysisName, organizationName }
 * @param {Object} analysisData - { id, name, companyName }
 * @param {string} lang - "fr" (default) or "en"
 */
export async function generateOperationalScenariosDocx(
  reportData = {},
  analysisData = {},
  lang = "fr",
) {
  const L = LABELS[lang] || LABELS.fr;
  const getSevLabel = lang === "en" ? getSeverityLabelEn : getSeverityLabelFr;
  const getLikLabel = lang === "en" ? getLikelihoodLabelEn : getLikelihoodLabelFr;
  const isEn = lang === "en";

  const {
    scenarios = [],
    attackPaths = [],
    analysisName = L.defaultAnalysis,
    organizationName = L.defaultOrg,
  } = reportData;

  const orgName =
    organizationName || analysisData.companyName || L.defaultOrg;

  // COVER PAGE
  const coverSection = buildCoverPage(
    L.coverTitle,
    L.coverSubtitle,
    orgName,
    analysisName,
  );

  // BODY
  const body = [];

  // Summary statistics
  const severityData = reportData.severityDistribution || {};
  const catastrophic = severityData.catastrophic || 0;
  const critical = severityData.critical || 0;
  const major = severityData.major || 0;
  const moderate = severityData.moderate || 0;
  const minor = severityData.minor || 0;
  const aiGenerated = scenarios.filter(
    (s) => s.source === "ai_generated",
  ).length;

  body.push(buildSectionTitle(L.execSummarySection, 1));
  body.push(buildInfoBox(L.nbScenariosBox, String(scenarios.length)));
  body.push(buildInfoBox(L.nbAttackPathsBox, String(attackPaths.length)));

  // EN-only summary boxes (kept from the former English generator)
  if (isEn) {
    body.push(
      buildInfoBox(
        L.highSeverityBox,
        String(catastrophic + critical + major),
      ),
    );
    body.push(buildInfoBox(L.aiGeneratedBox, String(aiGenerated)));
  }

  body.push(buildSpacer(400));

  body.push(buildPageBreak());

  // Scenarios details
  body.push(buildSectionTitle(L.scenariosSection, 1));

  if (scenarios.length === 0) {
    body.push(
      buildParagraph(L.emptyMsg, {
        italic: true,
        color: "9CA3AF",
      }),
    );
  } else {
    scenarios.forEach((scenario, index) => {
      const scenarioName = isEn
        ? scenario.nameEn || scenario.name
        : scenario.name;
      body.push(
        buildSectionTitle(
          `${index + 1}. ${scenarioName || `${L.scenarioWord} ${index + 1}`}`,
          2,
        ),
      );

      body.push(
        buildInfoBox(
          L.severityBox,
          getSevLabel(normalizeRiskLevel(scenario.severity)),
        ),
      );
      body.push(
        buildInfoBox(
          L.likelihoodBox,
          getLikLabel(normalizeRiskLevel(scenario.likelihood)),
        ),
      );
      if (scenario.timeline)
        body.push(buildInfoBox(L.durationBox, scenario.timeline));
      const attackPathsList = reportData.attackPaths || [];
      const actualAttackPath = attackPathsList.find(
        (ap) => ap.id === scenario.attackPathId,
      );
      const displayReference = actualAttackPath
        ? actualAttackPath.referenceCode || actualAttackPath.name
        : scenario.attackPathReference;

      if (displayReference)
        body.push(buildInfoBox(L.attackPathBox, displayReference));
      if (scenario.source === "ai_generated")
        body.push(buildInfoBox(L.sourceBox, L.generatedByAI));

      // Context section - pull from actualAttackPath (ID-based), fallback to scenario.context
      if (scenario.context || actualAttackPath) {
        body.push(buildSpacer(100));
        body.push(
          buildParagraph(L.contextHeading, {
            bold: true,
            color: "1E40AF",
          }),
        );

        const sourceRisk =
          actualAttackPath?.sourceRiskName || scenario.context?.sourceRisk;
        const targetObjective =
          actualAttackPath?.objectifVise ||
          actualAttackPath?.targetObjective ||
          scenario.context?.targetObjective;
        const dreadedEvent =
          actualAttackPath?.dreadedEventName || scenario.context?.dreadedEvent;
        const businessValue =
          actualAttackPath?.businessValueName ||
          scenario.context?.businessAssets;

        if (sourceRisk) body.push(buildInfoBox(L.threatSource, sourceRisk));
        if (targetObjective)
          body.push(buildInfoBox(L.targetObjective, targetObjective));
        if (dreadedEvent)
          body.push(
            buildInfoBox(L.dreadedEvent, dreadedEvent),
          );
        if (businessValue) {
          const assets = Array.isArray(businessValue)
            ? businessValue.map((a) => a.name || a).join(", ")
            : businessValue;
          body.push(buildInfoBox(L.supportingAssets, assets));
        }
      }

      const description = isEn
        ? scenario.descriptionEn || scenario.description
        : scenario.description;
      if (description) {
        body.push(buildSpacer(100));
        body.push(buildParagraph(L.descriptionHeading, { bold: true }));
        body.push(buildParagraph(description));
      }

      // Elementary Actions (Modes Op\u00e9ratoires)
      const elementaryActions = extractElementaryActions(scenario);
      if (elementaryActions.length > 0) {
        body.push(buildSpacer(200));
        body.push(
          buildParagraph(L.elementaryActionsHeading, { bold: true }),
        );

        const phases = ["CONNAITRE", "RENTRER", "TROUVER", "EXPLOITER", "AGIR"];

        phases.forEach((phase) => {
          const phaseActions = elementaryActions.filter(
            (ea) => (ea.phase || "").toUpperCase() === phase,
          );
          if (phaseActions.length === 0) return;

          body.push(buildSpacer(100));
          body.push(
            buildParagraph(
              `${L.phasePrefix}${L.phaseLabels[phase] || phase}`,
              {
                bold: true,
                color: "1E40AF",
              },
            ),
          );

          const rows = phaseActions.map((ea) => [
            isEn
              ? ea.techniqueEn || ea.technique || ea.nameEn || ea.name || L.dash
              : ea.technique || ea.name || L.dash,
            isEn
              ? ea.descriptionEn || ea.description || L.dash
              : ea.description || L.dash,
            ea.tools && ea.tools.length > 0
              ? ea.tools.slice(0, 3).join(", ")
              : L.dash,
            ea.vraisemblance !== undefined
              ? String(ea.vraisemblance)
              : ea.successProbability !== undefined
                ? String(ea.successProbability)
                : L.dash,
          ]);

          body.push(buildTable(L.actionsHeaders, rows, [2000, 3960, 1600, 800]));
        });

        // Visual flow diagram of modes op\u00e9ratoires — only retained modes are exported
        const allModes =
          scenario.modesOperatoires && scenario.modesOperatoires.length > 0
            ? scenario.modesOperatoires
            : generateModesOperatoiresFromActions(elementaryActions);
        const modesOp = (allModes || []).filter((m) => m && m.retained !== false);

        if (modesOp && modesOp.length > 0) {
          body.push(buildSpacer(200));
          const flowElements = buildModesOperatoiresFlow(
            modesOp,
            elementaryActions,
            { lang },
          );
          flowElements.forEach((el) => body.push(el));
        }
      }

      // Tools
      if (scenario.tools && scenario.tools.length > 0) {
        body.push(buildSpacer(200));
        body.push(buildParagraph(L.toolsHeading, { bold: true }));
        scenario.tools.forEach((t) =>
          body.push(buildBullet(isEn ? t.nameEn || t.name || t : t.name || t)),
        );
      }

      // Countermeasures
      if (scenario.countermeasures && scenario.countermeasures.length > 0) {
        body.push(buildSpacer(200));
        body.push(
          buildParagraph(L.countermeasuresHeading, { bold: true }),
        );
        scenario.countermeasures.forEach((cm) =>
          body.push(
            buildBullet(
              typeof cm === "string"
                ? cm
                : isEn
                  ? cm.descriptionEn ||
                    cm.nameEn ||
                    cm.description ||
                    cm.name ||
                    JSON.stringify(cm)
                  : cm.description || cm.name || JSON.stringify(cm),
            ),
          ),
        );
      }

      // Page break between scenarios (not after last one)
      if (index < scenarios.length - 1) {
        body.push(buildPageBreak());
      }
    });
  }

  // Attack paths appendix
  if (attackPaths.length > 0) {
    body.push(buildPageBreak());
    body.push(buildSectionTitle(L.appendixSection, 1));
    body.push(buildParagraph(L.appendixIntro));
    body.push(buildSpacer(200));

    const apRows = attackPaths.map((ap, i) => [
      ap.referenceCode || `CA${String(i + 1).padStart(2, "0")}`,
      isEn
        ? ap.sourceRiskNameEn ||
          ap.sourceRiskName ||
          ap.nameEn ||
          ap.name ||
          L.dash
        : ap.sourceRiskName || ap.name || L.dash,
      isEn
        ? ap.targetObjectiveEn || ap.objectifVise || ap.targetObjective || L.dash
        : ap.objectifVise || ap.targetObjective || L.dash,
      getSevLabel(normalizeRiskLevel(ap.severity)),
    ]);

    body.push(buildTable(L.apHeaders, apRows, [1300, 2800, 2800, 1460]));
  }

  const doc = buildDocument(
    L.docTitle,
    [coverSection, body],
    L.footer,
  );

  const ts = new Date().toISOString().slice(0, 10);
  await downloadDocx(doc, `${L.fileBase}_${orgName}_${ts}`);

  return { success: true };
}

export default generateOperationalScenariosDocx;
